import { IRegistry } from '../registry/iface/iRegistry.js';
import type { IData } from '../registry/iface/iData.js';
import type { IView } from '../registry/iface/iView.js';
import type { IAction } from '../registry/iface/iAction.js';
import type { IDataReloadable } from '../registry/iface/iDataReloadable.js';
import type { IDataSource } from '../registry/iface/iDataSource.js';
import { BaseView } from './dynamicView/baseView.js';
import { RuleView } from './dynamicView/ruleView.js';
import { HandlerView } from './dynamicView/handlerView.js';
import { Logger } from '@arshu/common/logger';

/**
 * Registers the SYSTEM handlers (BaseView, RuleView, HandlerView) — owned by the Assembler library — one of the only two handler kinds a client tier needs
 * (the other being client handlers, see RegisterClient).
 *
 * Design:
 * View registration lives in its own registrar so a client host can register views WITHOUT pulling in the
 * server registrar's filesystem-backed data providers. Registering RegisterBase or RegisterServer just to
 * obtain views would drag ConfigFile (and therefore file IO) into the browser bundle. Server hosts register
 * this alongside RegisterBase/RegisterClient/RegisterServer; the browser registers only
 * RegisterSystem + RegisterClient.
 */
export class RegisterSystem extends IRegistry {
    _registeringView: boolean = false;

    /**
     * Registers the system view handlers into the supplied registry map.
     */
    registerView(dynamicViewList: Map<string, IView>): void {
        try {
            if (this._registeringView === false) {
                this._registeringView = true;
                let key = '';

                key = 'BASEVIEW';
                if (dynamicViewList.has(key) === true) {
                    dynamicViewList.delete(key);
                }
                dynamicViewList.set(key, new BaseView());

                key = 'RULEVIEW';
                if (dynamicViewList.has(key) === true) {
                    dynamicViewList.delete(key);
                }
                dynamicViewList.set(key, new RuleView());

                key = 'HANDLERVIEW';
                if (dynamicViewList.has(key) === true) {
                    dynamicViewList.delete(key);
                }
                dynamicViewList.set(key, new HandlerView());

                this._registeringView = false;
            }
        } catch (ex) {
            Logger.error((ex as Error).message, 'RegistrySystem');
        }
    }

    /**
     * No data handlers — this registrar registers views only.
     */
    registerData(dynamicDataList: Map<string, IData>): void {
        void dynamicDataList;
    }

    /**
     * No action handlers — this registrar registers views only.
     */
    registerAction(dynamicActionList: Map<string, IAction>): void {
        void dynamicActionList;
    }

    /**
     * No app-data-backed runtime services — this registrar registers views only.
     */
    registerReloadable(appDataReloadables: Map<string, IDataReloadable[]>): void {
        void appDataReloadables;
    }

    /**
     * No capture-source resolvers — this registrar registers views only.
     */
    registerDataSource(dataSources: Map<string, IDataSource>): void {
        void dataSources;
    }
}
